import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AdminNav } from "@/components/admin/AdminNav";
import { CrmTabs } from "@/components/admin/CrmTabs";
import { api, money } from "@/lib/api";
import { inputCls, Field, Btn, Th, Td, TableWrap, EmptyRow } from "@/components/admin/ui";

const Detail = ({ label, value }) => (
  <div>
    <p className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500">{label}</p>
    <p className="text-sm text-neutral-200 mt-1">{value || "—"}</p>
  </div>
);

export default function CrmCarrierProfile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [editing, setEditing] = useState(false);
  const [edit, setEdit] = useState({});

  const load = useCallback(() => {
    api.get(`/crm/carriers/${id}/profile`).then(({ data }) => {
      setData(data);
      setEdit(data.carrier);
    }).catch(() => toast.error("Carrier not found"));
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/crm/carriers/${id}`, {
        owner_name: edit.owner_name, company: edit.company, email: edit.email, phone: edit.phone,
        mc_number: edit.mc_number, dot_number: edit.dot_number, equipment: edit.equipment,
        preferred_lanes: edit.preferred_lanes, factoring_company: edit.factoring_company,
        availability: edit.availability, notes: edit.notes, fee_pct: Number(edit.fee_pct),
      });
      toast.success("Carrier updated");
      setEditing(false);
      load();
    } catch {
      toast.error("Update failed");
    }
  };

  if (!data) {
    return (
      <div className="min-h-screen bg-[#0B0B0B] text-white">
        <AdminNav /><CrmTabs />
        <main className="max-w-7xl mx-auto px-4 py-8"><p className="text-neutral-500 font-mono text-xs uppercase tracking-[0.25em]">Loading...</p></main>
      </div>
    );
  }

  const { carrier: c, loads, documents, followups, invoices } = data;
  const set = (k) => (e) => setEdit({ ...edit, [k]: e.target.value });
  const paidTotal = invoices.reduce((s, i) => s + i.paid_total, 0);
  const owedTotal = invoices.reduce((s, i) => s + i.balance, 0);

  return (
    <div className="min-h-screen bg-[#0B0B0B] text-white" data-testid="crm-carrier-profile">
      <AdminNav />
      <CrmTabs />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <a href="/admin/crm/carriers" data-testid="profile-back-link" className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-white">← All Carriers</a>

        <div className="flex flex-wrap items-start justify-between gap-4 mt-4 mb-8">
          <div>
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-500" data-testid="profile-code">{c.carrier_code}</p>
            <h1 className="font-display font-bold uppercase tracking-tight text-2xl sm:text-4xl" data-testid="profile-company">{c.company}</h1>
            <p className="text-neutral-400 mt-1">{c.owner_name}</p>
          </div>
          <div className="flex items-center gap-3">
            <a
              href={`/sites/${(c.carrier_code || "").toLowerCase()}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="profile-website-link"
              className="font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 border border-white/30 text-neutral-300 hover:bg-white hover:text-black transition-colors duration-300"
            >
              Website ↗
            </a>
            <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-3 py-1.5 ${c.availability === "available" ? "bg-white text-black" : "border border-white/30 text-neutral-300"}`} data-testid="profile-availability">
              {c.availability?.replace("_", " ")}
            </span>
            <Btn data-testid="profile-edit-toggle" onClick={() => setEditing(!editing)}>{editing ? "Cancel" : "Edit"}</Btn>
          </div>
        </div>

        {editing ? (
          <form onSubmit={saveEdit} className="border border-white/15 bg-[#101010] p-5 sm:p-7 mb-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="profile-edit-form">
            <Field label="Owner Name" id="e-owner"><input id="e-owner" data-testid="edit-owner-input" value={edit.owner_name || ""} onChange={set("owner_name")} className={inputCls} /></Field>
            <Field label="Company" id="e-company"><input id="e-company" data-testid="edit-company-input" value={edit.company || ""} onChange={set("company")} className={inputCls} /></Field>
            <Field label="Email" id="e-email"><input id="e-email" data-testid="edit-email-input" value={edit.email || ""} onChange={set("email")} className={inputCls} /></Field>
            <Field label="Phone" id="e-phone"><input id="e-phone" data-testid="edit-phone-input" value={edit.phone || ""} onChange={set("phone")} className={inputCls} /></Field>
            <Field label="MC Number" id="e-mc"><input id="e-mc" data-testid="edit-mc-input" value={edit.mc_number || ""} onChange={set("mc_number")} className={inputCls} /></Field>
            <Field label="DOT Number" id="e-dot"><input id="e-dot" data-testid="edit-dot-input" value={edit.dot_number || ""} onChange={set("dot_number")} className={inputCls} /></Field>
            <Field label="Equipment" id="e-equip"><input id="e-equip" data-testid="edit-equipment-input" value={edit.equipment || ""} onChange={set("equipment")} className={inputCls} /></Field>
            <Field label="Preferred Lanes" id="e-lanes"><input id="e-lanes" data-testid="edit-lanes-input" value={edit.preferred_lanes || ""} onChange={set("preferred_lanes")} className={inputCls} /></Field>
            <Field label="Factoring Company" id="e-factoring"><input id="e-factoring" data-testid="edit-factoring-input" value={edit.factoring_company || ""} onChange={set("factoring_company")} className={inputCls} /></Field>
            <Field label="Availability" id="e-avail">
              <select id="e-avail" data-testid="edit-availability-select" value={edit.availability || "available"} onChange={set("availability")} className={`${inputCls} bg-[#101010]`}>
                <option value="available">available</option>
                <option value="under_load">under load</option>
                <option value="off">off</option>
              </select>
            </Field>
            <Field label="Dispatch Fee %" id="e-fee"><input id="e-fee" data-testid="edit-fee-input" type="number" step="0.1" min="0" max="100" value={edit.fee_pct ?? 9} onChange={set("fee_pct")} className={inputCls} /></Field>
            <Field label="Notes" id="e-notes"><input id="e-notes" data-testid="edit-notes-input" value={edit.notes || ""} onChange={set("notes")} className={inputCls} /></Field>
            <div className="lg:col-span-3"><Btn type="submit" data-testid="edit-save-button">Save Changes</Btn></div>
          </form>
        ) : (
          <div className="border border-white/10 p-5 sm:p-7 mb-8 grid grid-cols-2 lg:grid-cols-4 gap-6" data-testid="profile-details">
            <Detail label="Email" value={c.email} />
            <Detail label="Phone" value={c.phone} />
            <Detail label="MC / DOT" value={[c.mc_number, c.dot_number].filter(Boolean).join(" / ")} />
            <Detail label="Equipment" value={c.equipment} />
            <Detail label="Preferred Lanes" value={c.preferred_lanes} />
            <Detail label="Factoring" value={c.factoring_company} />
            <Detail label="Dispatch Fee" value={`${c.fee_pct}%`} />
            <Detail label="Notes" value={c.notes} />
          </div>
        )}

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8" data-testid="profile-fee-report">
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl">{loads.length}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Lifetime Loads</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl">{money(loads.reduce((s, l) => s + (l.gross_rate || 0), 0))}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Lifetime Gross Revenue</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl" data-testid="profile-fees-billed">{money(invoices.reduce((s, i) => s + i.amount, 0))}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Fees Billed</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl">{money(paidTotal)}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Fees Paid</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl">{money(owedTotal)}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Balance Owed</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl" data-testid="profile-invoices-settled">{invoices.filter((i) => i.status === "paid").length}/{invoices.length}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Invoices Settled</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl" data-testid="profile-reliability">{invoices.length ? `${Math.round((invoices.filter((i) => i.status === "paid").length / invoices.length) * 100)}%` : "—"}</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Payment Reliability</div></div>
          <div className="border border-white/10 p-4"><div className="font-display font-bold text-2xl">{c.fee_pct}%</div><div className="font-mono text-[9px] uppercase tracking-[0.25em] text-neutral-500 mt-1">Dispatch Fee Rate</div></div>
        </div>

        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mb-3">Loads</h2>
        <TableWrap testid="profile-loads-table">
          <thead><tr className="border-b border-white/10"><Th>Load</Th><Th>Broker</Th><Th>Route</Th><Th>Gross</Th><Th>RPM</Th><Th>Fee</Th><Th>Status</Th></tr></thead>
          <tbody>
            {loads.length === 0 ? <EmptyRow cols={7}>No loads</EmptyRow> : loads.map((l) => (
              <tr key={l.id} className="border-b border-white/5" data-testid={`profile-load-${l.id}`}>
                <Td className="font-mono text-xs text-neutral-400">{l.load_no}</Td>
                <Td>{l.broker}</Td>
                <Td className="text-neutral-300">{l.pickup} → {l.delivery}</Td>
                <Td className="font-mono text-xs">{money(l.gross_rate)}</Td>
                <Td className="font-mono text-xs">{l.rpm ? `$${l.rpm}` : "—"}</Td>
                <Td className="font-mono text-xs">{money(l.fee_amount)}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{l.status?.replace("_", " ")}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>

        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-10 mb-3">Documents</h2>
        <TableWrap testid="profile-docs-table">
          <thead><tr className="border-b border-white/10"><Th>Type</Th><Th>File</Th><Th>Expiry</Th><Th>Uploaded</Th></tr></thead>
          <tbody>
            {documents.length === 0 ? <EmptyRow cols={4}>No documents</EmptyRow> : documents.map((d) => (
              <tr key={d.id} className="border-b border-white/5">
                <Td><span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-1 bg-white/10">{d.doc_type}</span></Td>
                <Td className="text-neutral-300 max-w-[200px] truncate">{d.original_filename}</Td>
                <Td className={`font-mono text-[10px] ${d.expires_at && d.expires_at < new Date().toISOString().slice(0, 10) ? "text-red-400" : "text-neutral-400"}`}>{d.expires_at || "—"}</Td>
                <Td className="font-mono text-xs text-neutral-500">{new Date(d.created_at).toLocaleDateString()}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
        <a href={`/admin/crm/documents`} data-testid="profile-upload-doc-link" className="inline-block mt-3 font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-400 hover:text-white underline underline-offset-4">
          Upload a document →
        </a>

        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-10 mb-3">Follow-ups</h2>
        <TableWrap testid="profile-followups-table">
          <thead><tr className="border-b border-white/10"><Th>Note</Th><Th>Next Action</Th><Th>Due</Th><Th>Priority</Th><Th>Status</Th></tr></thead>
          <tbody>
            {followups.length === 0 ? <EmptyRow cols={5}>No follow-ups</EmptyRow> : followups.map((f) => (
              <tr key={f.id} className={`border-b border-white/5 ${f.overdue ? "bg-red-400/5" : ""}`}>
                <Td className="max-w-[240px]">{f.note}</Td>
                <Td className="text-neutral-400">{f.next_action || "—"}</Td>
                <Td className={`font-mono text-[10px] ${f.overdue ? "text-red-400" : "text-neutral-400"}`}>{f.overdue ? "Overdue · " : ""}{f.due_date || "—"}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{f.priority}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-500">{f.done ? "Done" : "Open"}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>

        <h2 className="font-mono text-[10px] uppercase tracking-[0.3em] text-neutral-400 mt-10 mb-3">Payment History</h2>
        <TableWrap testid="profile-invoices-table">
          <thead><tr className="border-b border-white/10"><Th>Invoice</Th><Th>Load</Th><Th>Amount</Th><Th>Paid</Th><Th>Balance</Th><Th>Status</Th></tr></thead>
          <tbody>
            {invoices.length === 0 ? <EmptyRow cols={6}>No invoices</EmptyRow> : invoices.map((inv) => (
              <tr key={inv.id} className="border-b border-white/5" data-testid={`profile-invoice-${inv.id}`}>
                <Td className="font-mono text-xs text-neutral-400">{inv.invoice_no}</Td>
                <Td className="font-mono text-xs text-neutral-400">{inv.load_no}</Td>
                <Td className="font-mono text-xs">{money(inv.amount)}</Td>
                <Td className="font-mono text-xs text-neutral-400">
                  {money(inv.paid_total)}
                  {inv.payments.length > 0 && (
                    <div className="text-[10px] text-neutral-600 mt-0.5">
                      {inv.payments.map((p) => `${money(p.amount)} ${p.method || ""}`.trim()).join(" · ")}
                    </div>
                  )}
                </Td>
                <Td className="font-mono text-xs" data-testid={`profile-invoice-balance-${inv.id}`}>{money(inv.balance)}</Td>
                <Td className="font-mono text-[9px] uppercase tracking-[0.2em] text-neutral-400">{inv.status}</Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      </main>
    </div>
  );
}
